/* eslint-disable */
import React, { useLayoutEffect, useRef } from 'react';
import { Ico } from './ShellIcons';
import fb from './filterBar.module.css';
import pop from './popovers.module.css';

// ============================================================
// StageSpectrum — counts and filter in ONE control (v3). Proportional 20px
// segments (a 9% floor so no stage vanishes), the name revealed on hover
// (the hovered segment opens to exactly its name + count as a pill in its own
// colour laid over the bar — the bar never reflows, so the pointer never lands
// on a neighbour; left-half segments open rightwards, the rest leftwards —
// the tooltip below adds the share), a dot on the selected ones, and a
// popover listing every stage with its share plus the "also tracked" rows.
// stages: [{ key, name, color, count }]; extras: the same shape, listed under a
// divider (No image · Imported). value: the Set of selected keys.
// ============================================================

const fmt = (n) => (n == null ? '' : Number(n).toLocaleString('en-US'));

export default function StageSpectrum({ label = 'Stage', stages, extras = [], total, value, onToggleKey, onClear, open, onToggle, onClose, scopeNote }) {
  const bar = useRef(null);
  const sel = value || new Set();
  const sum = total ?? stages.reduce((a, s) => a + (s.count || 0), 0);
  const floor = Math.round(sum * 0.09);
  const max = Math.max(1, ...stages.map((s) => s.count || 0));
  const on = sel.size > 0;
  // narrow segments hide their number (measured, not guessed)
  useLayoutEffect(() => {
    if (!bar.current) return;
    Array.from(bar.current.children).forEach((seg) => seg.classList.toggle(pop.narrow, seg.offsetWidth < 34));
  });
  const pct = (n) => (sum ? Math.round((n / sum) * 100) : 0);
  // which way each segment's hover pill opens: by the weighted share before it
  const weights = stages.map((st) => Math.max(st.count || 0, floor));
  const wTotal = weights.reduce((a, b) => a + b, 0) || 1;
  let acc = 0;
  const side = weights.map((wt) => { const mid = (acc + wt / 2) / wTotal; acc += wt; return mid < 0.5 ? 'l' : 'r'; });
  return (
    <div className={fb.ffw} data-ffw="spec">
      <div className={`${pop.spec} ${on ? pop.on : ''} ${open ? pop.open : ''}`} data-spec>
        <span className={pop.specL}>{label}</span>
        <div className={`${pop.specBar} ${on ? pop.filt : ''}`} ref={bar} role="group" aria-label={`${label} distribution — click to filter`}>
          {stages.map((st, i) => (
            <button key={st.key} type="button" data-side={side[i]} className={`${pop.segm} ${sel.has(st.key) ? pop.on : ''}`} aria-pressed={sel.has(st.key)} style={{ '--f': Math.max(st.count || 0, floor), '--c': st.color }} onClick={(e) => { e.stopPropagation(); onToggleKey(st.key); }}>
              <span className={pop.segIn}>
                <span className={pop.sn} data-seg-name>{st.name}</span>
                <span className={pop.sl}>{fmt(st.count || 0)}</span>
              </span>
              <span className={pop.tip}><b>{st.name}</b> · {fmt(st.count || 0)} · {pct(st.count || 0)}%</span>
            </button>
          ))}
        </div>
        <button type="button" className={pop.specMore} onClick={(e) => { e.stopPropagation(); onToggle(); }} aria-expanded={open} title="All stages, with shares"><Ico name="chev" /></button>
        {open && (
          <div className={`${pop.fpop} ${pop.specPop}`} data-shell-pop onClick={(e) => e.stopPropagation()}>
            <div className={pop.fpHead}><span className={pop.fpT}>Stages</span><span className={pop.fpN}>{fmt(sum)} {scopeNote || 'on the board'}{on ? <> · <b>{sel.size} on</b></> : null}</span></div>
            {stages.map((st) => <Row key={st.key} st={st} on={sel.has(st.key)} w={Math.round(((st.count || 0) / max) * 100)} pct={pct(st.count || 0)} onClick={() => onToggleKey(st.key)} />)}
            {extras.length > 0 && <div className={pop.mdiv} />}
            {extras.map((st) => <Row key={st.key} st={st} on={sel.has(st.key)} w={Math.round(((st.count || 0) / Math.max(1, sum)) * 100)} pct={pct(st.count || 0)} onClick={() => onToggleKey(st.key)} />)}
            <div className={pop.ffoot}>
              <button type="button" onClick={onClear}>Clear</button>
              <span className={pop.fpHint}>hover a segment for its name</span>
              <button type="button" className={pop.go} onClick={onClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ st, on, w, pct, onClick }) {
  return (
    <button type="button" className={`${pop.specRow} ${on ? pop.on : ''}`} aria-pressed={on} style={{ '--c': st.color, '--w': `${w}%` }} onClick={onClick}>
      <span className={pop.ck}><Ico name="check" /></span>
      <span className={pop.nm}>{st.name}<i /></span>
      <span className={pop.k}>{fmt(st.count || 0)}<small>{pct}%</small></span>
    </button>
  );
}
